import { api } from './api'
import type {
  DashboardFilters,
  DimOffice,
  GovdataResponse,
  PaginatedGovdataResponse,
  OfficePerformance,
  AcqPerformanceResponse,
  CompetitionHealth,
  SpeedToContract,
  SmallBizEngagement,
  ContractTypeTrend,
  VendorConcentration,
  FiscalYearEndSurge,
  ContractDuration,
} from '../types/govdata'

function toParams(filters: DashboardFilters, extra?: Record<string, any>): Record<string, any> {
  return {
    ...filters,
    ...extra,
  }
}

// ---------------------------------------------------------------------------
// Performance fact response type (what the backend actually returns)
// ---------------------------------------------------------------------------
interface PerfFactRow {
  [key: string]: any
}

interface PerfFactResponse {
  results: PerfFactRow[]
  meta: { group_by: string[]; row_count: number }
}

/**
 * Call the universal performance endpoint and adapt the response to the
 * old GovdataResponse shape that section components expect.
 */
async function perfQuery<T>(
  filters: DashboardFilters,
  extra?: Record<string, any>,
  adapt?: (row: PerfFactRow) => T,
): Promise<GovdataResponse<T>> {
  const resp = await api.getRaw<PerfFactResponse>(
    '/api/govdata/performance/',
    toParams(filters, extra),
  )
  const results = adapt
    ? resp.results.map(adapt)
    : (resp.results as unknown as T[])
  // Return empty filters since performance endpoint uses meta instead
  return { results, filters: { fiscal_years: [], sub_agencies: [] } }
}

/**
 * Adapt performance_fact row to legacy shape: rename award_count → total_awards,
 * and add total_obligated as string to match old types.
 */
function adaptLegacy(row: PerfFactRow): PerfFactRow {
  return {
    ...row,
    total_awards: row.award_count,
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const govdataAPI = {
  dimOffices() {
    return api.getRaw<{ results: DimOffice[] }>('/api/govdata/dim-offices/')
  },

  // -- Performance-fact backed (most Exec dashboard measures) --

  officePerformance(filters: DashboardFilters, extra?: { fiscal_quarter?: number }) {
    return perfQuery<OfficePerformance>(
      filters,
      { group_by: 'fiscal_year,fiscal_quarter', ...extra },
      (r) => adaptLegacy(r) as unknown as OfficePerformance,
    )
  },

  competitionHealth(filters: DashboardFilters) {
    return perfQuery<CompetitionHealth>(
      filters,
      { group_by: 'fiscal_year' },
      (r) => adaptLegacy(r) as unknown as CompetitionHealth,
    )
  },

  smallBiz(filters: DashboardFilters) {
    return perfQuery<SmallBizEngagement>(
      filters,
      { group_by: 'fiscal_year' },
      (r) => adaptLegacy(r) as unknown as SmallBizEngagement,
    )
  },

  contractTypes(filters: DashboardFilters, extra?: { vehicle_category?: string; pricing_category?: string }) {
    return perfQuery<ContractTypeTrend>(
      filters,
      { group_by: 'fiscal_year,vehicle_category', ...extra },
      (r) => ({ ...r, award_count: r.award_count ?? 0 }) as unknown as ContractTypeTrend,
    )
  },

  otPrevalence(params: { hierarchy_level?: string; parent_code?: string; fiscal_year?: number }) {
    // OT prevalence uses hierarchy rollup on the performance endpoint
    return api.getRaw<AcqPerformanceResponse>(
      '/api/govdata/performance/',
      {
        ...params,
        group_by: 'fiscal_year,is_ot',
      },
    )
  },

  acqPerformance(params: {
    hierarchy_level?: string
    parent_code?: string
    fiscal_year?: number
    sort_by?: string
  }) {
    return api.getRaw<AcqPerformanceResponse>(
      '/api/govdata/performance/',
      {
        ...params,
        group_by: 'fiscal_year',
      },
    )
  },

  // -- Separate-table backed (not in performance_fact) --

  speedToContract(filters: DashboardFilters, extra?: { extent_competed?: string; idv_type?: string }) {
    return api.getRaw<GovdataResponse<SpeedToContract>>(
      '/api/govdata/speed-to-contract/',
      toParams(filters, extra),
    )
  },

  vendorConcentration(filters: DashboardFilters, extra?: { page?: number; page_size?: number }) {
    return api.getRaw<PaginatedGovdataResponse<VendorConcentration>>(
      '/api/govdata/vendor-concentration/',
      toParams(filters, extra),
    )
  },

  fyEndSurge(filters: DashboardFilters) {
    return api.getRaw<GovdataResponse<FiscalYearEndSurge>>(
      '/api/govdata/fy-end-surge/',
      toParams(filters),
    )
  },

  contractDuration(filters: DashboardFilters, extra?: { vehicle_category?: string; pricing_category?: string }) {
    return api.getRaw<GovdataResponse<ContractDuration>>(
      '/api/govdata/contract-duration/',
      toParams(filters, extra),
    )
  },
}
